/**
 * Live Activity do Modo Foco — Ilha Dinâmica + tela de bloqueio (iOS 16.2+).
 *
 * Blueprint: `docs/systems/focus-live-activity.md`. Este módulo é o ÚNICO
 * importador do plugin `capacitor-live-activities` (guard estrutural em
 * `tests/unit/focusLiveActivityConfig.test.ts`) e o dono único do ciclo de
 * vida da activity.
 *
 * ── As três regras que não podem cair ───────────────────────────────────────
 *
 * 1. **Falha aqui NUNCA alcança a sessão.** Live Activity é mostrador de
 *    sistema; a sessão de Foco é economia (moedas, XP, liga). Toda chamada ao
 *    plugin é engolida com `console.warn` — o pior estado possível é "a Ilha
 *    não apareceu", nunca "a sessão quebrou".
 *
 * 2. **ZERO updates periódicos.** O ActivityKit anima contagem sozinho a
 *    partir de datas (`countdown` até `endTime`; `timer` conta para cima).
 *    Só pausar/retomar/encerrar tocam na activity — e como o layout mora nos
 *    ATTRIBUTES (imutáveis após o start), pausa e retomada são
 *    encerrar-e-reabrir, não update (§3.4 do blueprint).
 *
 * 3. **O Proxy do plugin não atravessa fronteira de promise** (BUG-154): a
 *    fachada abaixo é um objeto plano com arrow methods, e o import é
 *    ESTÁTICO (o `import()` dinâmico pendurou no WKWebView nos builds 41/42).
 *    Quem impede web/Android de falar com o plugin é o guard de plataforma.
 *
 * ── Reconciliação, não fiação por evento ────────────────────────────────────
 *
 * `syncFocusLiveActivity` recebe uma FOTO do estado (sessão + pausa) e faz a
 * activity convergir. O chamador é um efeito no FocusModeModal — o miolo do
 * `useFocusTimer` não sabe que a Ilha existe, e o unmount do modal NÃO
 * encerra nada (a sessão continua; a Ilha é exatamente para quando o app
 * some da frente).
 *
 * Relançamento do app com activity viva: o plugin re-registra as activities
 * existentes no load, e o par {activityId, sessionId, paused} persiste em
 * `localStorage` — sessão igual ADOTA a activity em vez de duplicar; sessão
 * diferente (ou nenhuma) encerra a órfã.
 *
 * ── 4. Uma convergência por vez, e encerrar é VARRER (BUG-163) ──────────────
 *
 * O chamador dispara com `void` e `useFocusTimer` grava a sessão duas vezes por
 * transição (otimista + servidor). Sem fila, as duas convergências se cruzavam
 * na janela em que a memória está vazia — entre encerrar e persistir a id nova
 * — e cada uma abria a sua activity; a última a persistir apagava a id da
 * outra, que ficava viva e inalcançável. Daí as cinco Ilhas empilhadas do
 * relato de 21/08.
 *
 * Duas consequências que valem escrever, porque a segunda parece redundante e
 * não é: (a) `syncFocusLiveActivity` serializa e coalesce — só a foto mais
 * recente converge; (b) encerrar varre `getAllActivities`, não só a id
 * lembrada, porque **o que já vazou não está em memória nenhuma** e sem a
 * varredura continuaria preso nos aparelhos depois do deploy.
 *
 * ── 5. A Ilha só NASCE com o app na frente — e quem não nasceu tenta de novo ─
 *
 * `Activity.request` é recusado com o app no fundo
 * (`ActivityAuthorizationError.visibility`), e a convergência que abre a
 * activity costuma chegar DEPOIS do toque em "Iniciar": ela espera a sessão
 * virar real no servidor e a capa assentar. Quem tocava e saía do app na mesma
 * respiração via a chamada morrer no fundo, o `catch` apagar a memória e
 * ninguém tentar de novo — *"às vezes a Ilha não é acionada"* (06/09). Três
 * regras, todas em `converge`:
 *
 *  (a) app no fundo → NÃO abre nem reabre: guarda a foto e espera a volta ao
 *      primeiro plano (`visibilitychange`), que dispara a convergência de novo;
 *  (b) a activity nova abre ANTES de varrer a anterior: menos latência até a
 *      Ilha aparecer, e se o start falhar a anterior (da mesma sessão) segue
 *      viva e certa — uma Ilha sem capa é melhor que nenhuma;
 *  (c) a Ilha COM capa nunca é trocada por uma SEM para a mesma foto (a capa
 *      só "chega"; uma foto sem capa depois de uma com capa é a conversão
 *      desistindo, ou o prazo do modal esgotando).
 */

import { LiveActivities } from "capacitor-live-activities";
import { nativePlatform } from "../../../lib/platform/native";
import { buildFocusActionUrl, type FocusDeepLinkAction } from "./focusDeepLink";
import {
  computeDueMs,
  computeEffectiveElapsedSec,
  MAX_FOCUS_PAUSE_SEC,
  MAX_FOCUS_SESSION_ELAPSED_SEC,
  type FocusPauseState,
} from "./focusElapsed";

/**
 * Interruptor de ROLLBACK — constante local, nunca config remota (mesma
 * doutrina do pushPlatformGate: config que chega vazia vira liberação
 * acidental). `false` = nenhuma chamada ao plugin; a extension fica inerte
 * no binário.
 */
export const FOCUS_LIVE_ACTIVITY_ENABLED = true;

const STORAGE_KEY = "livra_focus_live_activity";

/* Paleta fixa da activity — superfície de SISTEMA, fora dos 21 temas do app
 * de propósito: o widget não tem acesso ao tema, e o par escuro+tinta clara
 * é o que lê bem sobre a Ilha (sempre preta) e sobre a tela de bloqueio. */
const PAPER = "#171310";
const INK = "#f4f1ec";
const SOFT = "#c9c0b4";
const ACCENT = "#e0a458";

export interface FocusLiveActivitySnapshot {
  sessionId: string;
  kind: "pomodoro" | "free" | "pages";
  paused: boolean;
  bookTitle: string | null;
  startedAtMs: number;
  pausedAtMs: number | null;
  pausedTotalSec: number;
  /** 0 = sessão livre/páginas (sem alvo cronometrado). */
  targetDurationSec: number;
  /**
   * Unidade da meta do modo páginas — ESPELHA a decisão do modal (Kindle com
   * `targetPercent` conta pontos percentuais; físico conta páginas). A unidade
   * viaja no snapshot em vez de ser rederivada aqui porque rederivar é como
   * duas superfícies divergem (BUG-81).
   */
  goalUnit: "pages" | "percent";
  /** Alvo do modo páginas na unidade acima. 0 = sem meta. */
  goalTarget: number;
  /**
   * Contador "+1" declarado até agora (mesma unidade da meta). Cada declaração
   * é um GESTO do usuário — reabre a activity como pausa/retomada/+5min fazem
   * (contentKey), o que NÃO é update periódico: acontece com o app na frente,
   * onde a Ilha nem renderiza, e o coalescing da fila absorve rajada de toque.
   */
  declaredCount: number;
  /**
   * Miniatura JPEG da capa em data URL (`focusCover.ts`), ou `null`.
   *
   * Viaja no payload porque o renderer aceita `image` com `base64` — o
   * blueprint dava a capa como dependente de App Group e a medição de 23/08
   * refutou (ver `focusCover.ts`). Chega DEPOIS do início da sessão (é uma
   * conversão de canvas), então a activity reabre uma vez quando ela assenta:
   * é gesto-equivalente, acontece com o app na frente e o coalescing da fila
   * absorve. Passa do orçamento → a capa cai e o layout usa o glifo.
   */
  coverDataUrl: string | null;
}

/* ── Fachada plana sobre o plugin (BUG-154) ─────────────────────────────── */

const liveActivitiesFacade = {
  start: async (options: Record<string, unknown>): Promise<string> => {
    const result = await LiveActivities.startActivity(
      options as Parameters<typeof LiveActivities.startActivity>[0],
    );
    return result.activityId;
  },
  end: async (activityId: string): Promise<void> => {
    await LiveActivities.endActivity({ activityId, data: {} });
  },
  /**
   * Ids de TODAS as activities vivas do app.
   *
   * ⚠️ O tipo publicado do pacote MENTE aqui: ele declara
   * `Promise<{activities: LiveActivitiesOptions[]}>`, e o Swift devolve
   * `{activities: [{id, state, systemId}]}` (`ios/Plugin/LiveActivities.swift:175`
   * — medido no fonte, não deduzido do `.d.ts`). Quem serve para `endActivity`
   * é o `id`: é a chave do dicionário que `syncExistingActivities()` repovoa a
   * partir de `Activity<DynamicActivityAttributes>.activities`, e é por isso
   * que esta lista alcança também as órfãs de um processo que morreu.
   *
   * Tolerante por construção: plugin sem o método, resposta em outro formato
   * ou rejeição viram lista vazia — aí a varredura degrada para "encerra só a
   * lembrada", que é exatamente o comportamento anterior ao BUG-163.
   */
  list: async (): Promise<string[]> => {
    const raw = (await LiveActivities.getAllActivities()) as unknown;
    const entries = (raw as { activities?: unknown })?.activities;
    if (!Array.isArray(entries)) return [];
    return entries
      .map((entry) =>
        entry && typeof entry === "object" && typeof (entry as { id?: unknown }).id === "string"
          ? (entry as { id: string }).id
          : null,
      )
      .filter((id): id is string => id !== null);
  },
};

/* ── Layouts (JSON DSL do plugin — chaves medidas no parser Swift) ───────── */

function text(content: string, props: Record<string, unknown>[]): Record<string, unknown> {
  return { type: "text", properties: [{ text: content }, ...props] };
}

/** Elemento de contagem: `countdown` até `endTime`, ou `timer` desde a âncora. */
function timerElement(
  snapshot: FocusLiveActivitySnapshot,
  props: Record<string, unknown>[],
): Record<string, unknown> {
  const pauseState: FocusPauseState = {
    startedAtMs: snapshot.startedAtMs,
    pausedAtMs: null, // o layout "rodando" só existe com a sessão destravada
    pausedTotalSec: snapshot.pausedTotalSec,
  };
  if (snapshot.targetDurationSec > 0) {
    // Pomodoro: o vencimento vem do MESMO dono que agenda a conclusão no
    // client (invariante 15 da skill) — pausa acumulada empurra o fim.
    const dueMs = computeDueMs(pauseState, snapshot.targetDurationSec);
    return {
      type: "timer",
      properties: [{ endTime: dueMs ?? snapshot.startedAtMs }, { style: "countdown" }, ...props],
    };
  }
  // Livre/páginas: conta PARA CIMA. A âncora desloca pelo tempo já pausado,
  // senão a contagem mostraria tempo de parede em vez de tempo focado.
  const anchorMs = snapshot.startedAtMs + snapshot.pausedTotalSec * 1000;
  return { type: "timer", properties: [{ endTime: anchorMs }, { style: "timer" }, ...props] };
}

function bookLine(snapshot: FocusLiveActivitySnapshot): string {
  return snapshot.bookTitle?.trim() || "Sessão de leitura";
}

/**
 * Linha da meta — SÓ no modo páginas, e na linguagem que o `FocusTimer` já
 * usa ("de N páginas" / "de X% da meta"). Pomodoro não ganha linha (o alvo
 * dele É o countdown) e sessão livre não tem alvo — estampar meta ali seria
 * inventar dado, mesmo que um chamador a passe por engano.
 */
export function goalLineOf(snapshot: FocusLiveActivitySnapshot): string | null {
  if (snapshot.kind !== "pages" || snapshot.goalTarget <= 0) return null;
  return snapshot.goalUnit === "percent"
    ? `${snapshot.declaredCount} de ${snapshot.goalTarget}% da meta`
    : `${snapshot.declaredCount} de ${snapshot.goalTarget} páginas`;
}

/**
 * Linha de CONTEXTO da sessão — o que ela é, na linguagem do `FocusSheet`
 * (05/09, relato *"não tem infos o suficiente ali"*).
 *
 * Pomodoro diz o alvo ("Pomodoro de 25 min"), livre diz que é livre, e o modo
 * páginas cede a vez à linha da meta (`goalLineOf`) quando há meta — duas
 * linhas dizendo a mesma coisa seria ruído no espaço mais curto do app. Sem
 * meta declarada ainda, o modo páginas diz o que é.
 */
export function sessionLineOf(snapshot: FocusLiveActivitySnapshot): string | null {
  if (snapshot.kind === "pages") return goalLineOf(snapshot) ? null : "Meta de páginas";
  if (snapshot.targetDurationSec > 0) {
    return `Pomodoro de ${Math.max(1, Math.round(snapshot.targetDurationSec / 60))} min`;
  }
  return "Sessão livre";
}

/**
 * Legenda da contagem — o que o número da Ilha está contando. Countdown lê
 * "restante"; contagem crescente lê "de leitura". A mesma palavra que o anel
 * do `FocusTimer` já usa sob o relógio ("restante"), para o card e a tela
 * dizerem a mesma coisa.
 */
export function timerCaptionOf(snapshot: FocusLiveActivitySnapshot): string {
  return snapshot.targetDurationSec > 0 ? "restante" : "de leitura";
}

/**
 * O que o card passa a dizer quando o iOS o marca OBSOLETO (BUG-204/F7).
 *
 * ── O buraco que isto fecha ─────────────────────────────────────────────────
 *
 * A sessão vence com o app no fundo — que é o caso NORMAL, é para isso que a
 * Ilha existe — e o card continua desenhando "em andamento": countdown parado
 * em 0:00 e um chip "Pausar" oferecendo pausar o que já acabou. A pessoa olha a
 * tela de bloqueio e não tem como saber que já pode registrar as páginas.
 *
 * ── Por que tudo isto viaja no START, e não no vencimento ───────────────────
 *
 * Porque no vencimento **não há ninguém para mandar**. A WebView está suspensa
 * (é a mesma medição que mantém a barra de progresso fora do card — blueprint
 * §4.6), então o `data` que o widget tem na hora do stale é o que foi enviado
 * na última convergência. Mandar a cópia de conclusão "quando concluir" é
 * escrever código que nunca roda.
 *
 * ── Um dono só para o INSTANTE e para o SENTIDO ─────────────────────────────
 *
 * `isStale` significa três coisas diferentes conforme o ramo que gerou o
 * `staleDate`, e o widget não tem como saber qual — nem deve saber, pela mesma
 * regra que já governa os controles (rótulo mora no TS; no Swift, mudar uma
 * palavra passaria a exigir build das lojas).
 *
 * Por isso instante e cópia saem da MESMA função: é estruturalmente impossível
 * o card dizer "concluída" num ramo cujo `staleDate` significa "fantasma".
 *
 *  · **pomodoro rodando** → vence em `computeDueMs`. Concluiu de verdade: a
 *    fase 1 credita e o que falta são as páginas.
 *  · **pausada** → a retomada expira em `MAX_FOCUS_PAUSE_SEC` (invariante 16).
 *    A pessoa NÃO perde o que leu: o elapsed congelou no instante da pausa.
 *  · **livre/páginas** → o teto de plausibilidade. Aí é fantasma, e a F6 acaba
 *    de estabelecer que fantasma é cancelada — não há o que coletar.
 *
 * ── Nenhuma ação nova, e isso é o ponto ─────────────────────────────────────
 *
 * O destino de cada ramo é um deep link que JÁ existe e JÁ leva ao wrap-up:
 * "encerrar" na sessão concluída (o wrap-up é onde as páginas são declaradas —
 * e numa sessão já creditada a chamada é idempotente pelo claim do BUG-170) e
 * "retomar" na pausa expirada (o servidor recusa a retomada e devolve
 * `expired: true`, que o `useFocusTimer` já roteia para o wrap-up).
 *
 * O que muda é só o RÓTULO — que é exatamente o modelo deste arquivo. Um verbo
 * novo de deep link aqui seria um comando sem efeito próprio: código morto com
 * aparência de mecanismo.
 */
export type FocusStaleKind = "done" | "pause_expired" | "abandoned";

export interface FocusStaleState {
  /** Instante do `staleDate` do ActivityKit, em ms de JS. */
  atMs: number;
  kind: FocusStaleKind;
  /** O que o card estampa no lugar do estado "em andamento". */
  title: string;
  /** `null` quando não há o que coletar — o chip some em vez de mentir. */
  actionLabel: string | null;
  actionUrl: string | null;
}

export function resolveStaleState(snapshot: FocusLiveActivitySnapshot): FocusStaleState {
  if (snapshot.paused && snapshot.pausedAtMs != null) {
    return {
      atMs: snapshot.pausedAtMs + MAX_FOCUS_PAUSE_SEC * 1000,
      kind: "pause_expired",
      title: "A pausa expirou",
      // "Registrar páginas" e não "Retomar": o destino é o mesmo link de
      // retomada, mas o que vai acontecer lá é o wrap-up. O rótulo descreve o
      // DESFECHO, que é o que a pessoa precisa saber — e é a diferença entre
      // este chip e o botão morto que o BUG-58 já custou.
      actionLabel: "Registrar páginas",
      actionUrl: buildFocusActionUrl("resume", snapshot.sessionId),
    };
  }

  const dueMs = computeDueMs(
    {
      startedAtMs: snapshot.startedAtMs,
      pausedAtMs: null,
      pausedTotalSec: snapshot.pausedTotalSec,
    },
    snapshot.targetDurationSec,
  );
  if (dueMs != null) {
    return {
      atMs: dueMs,
      kind: "done",
      title: "Sessão concluída",
      actionLabel: "Registrar páginas",
      actionUrl: buildFocusActionUrl("end", snapshot.sessionId),
    };
  }

  return {
    atMs: snapshot.startedAtMs + (MAX_FOCUS_SESSION_ELAPSED_SEC + snapshot.pausedTotalSec) * 1000,
    kind: "abandoned",
    title: "Sessão expirada",
    // Sem chip: a sessão livre que passou do teto é fantasma, e a F6 a cancela
    // no próximo "Iniciar". Oferecer "Registrar páginas" ali prometeria um
    // wrap-up que não vai existir.
    actionLabel: null,
    actionUrl: null,
  };
}

/**
 * Instante em que o conteúdo do card deixa de ser verdade se o app nunca mais
 * o tocar — vira o `staleDate` do ActivityKit (o iOS marca o card como
 * obsoleto; sem ele, sessão que morreu com o app deixa um card "fresco"
 * mentindo na tela de bloqueio até o teto de 8h do sistema).
 *
 * Fina de propósito: cada ramo vem do DONO da regra correspondente, nunca de
 * literal, e desde a F7 o instante sai do MESMO lugar que a cópia do estado
 * obsoleto (`resolveStaleState`) — repetir os três ramos aqui seria a segunda
 * cópia, a que envelhece sozinha.
 */
export function staleDateMsOf(snapshot: FocusLiveActivitySnapshot): number {
  return resolveStaleState(snapshot).atMs;
}

/** Minutos focados congelados no instante da pausa — para o rótulo estático. */
export function pausedMinutesOf(snapshot: FocusLiveActivitySnapshot, nowMs: number): number {
  const elapsed = computeEffectiveElapsedSec(
    {
      startedAtMs: snapshot.startedAtMs,
      pausedAtMs: snapshot.pausedAtMs,
      pausedTotalSec: snapshot.pausedTotalSec,
    },
    nowMs,
  );
  return Math.max(0, Math.floor(elapsed / 60));
}

/**
 * Glifo de estado — o que aparece quando não há capa (e no `minimal` da Ilha,
 * onde a região é um círculo de ~22pt e a capa não caberia inteira).
 *
 * `resizable` + `contentMode` são obrigatórios: sem eles o Kit desenha o SF
 * Symbol no tamanho intrínseco DENTRO do frame, e o `width`/`height` viram
 * recorte em vez de escala.
 */
function glyphElement(running: boolean, size: number): Record<string, unknown> {
  return {
    type: "image",
    properties: [
      { systemName: running ? "book.closed.fill" : "pause.fill" },
      { color: ACCENT },
      { resizable: true },
      { contentMode: "fit" },
      { width: size },
      { height: size },
    ],
  };
}

/**
 * Capa do livro, com dois desvios OBRIGATÓRIOS medidos no renderer:
 *
 * 1. **`cornerRadius` NÃO vai na imagem.** O Kit o implementa como
 *    `clipShape(Circle().inset(by: -radius))` — um CÍRCULO, não um retângulo
 *    arredondado (`JSONLayoutParser+ImageView.swift`). Passá-lo ali recortaria
 *    a capa em bolinha. O arredondamento vem do container em volta, onde o
 *    modificador genérico usa `.cornerRadius()` de verdade
 *    (`JSONLayoutParser+BuildView.swift:180`).
 * 2. **`contentMode` é `fit`, nunca `fill`.** O Kit aplica `aspectRatio(...)`
 *    e **não** chama `.clipped()` depois, então `fill` transborda o frame e
 *    invade o texto ao lado. A miniatura já sai do canvas na proporção exata
 *    (`focusCover.ts` recorta em 60×90), então `fit` preenche sem sobra.
 * 3. **O base64 NÃO é embutido aqui — vai por referência.** A capa aparece em
 *    TRÊS superfícies (tela de bloqueio, Ilha compacta, Ilha expandida), e
 *    embutir os ~1,5 KB nas três triplicava o payload e estourava o orçamento
 *    sozinha (medido: a capa era descartada em 100% dos casos). O parser
 *    resolve `{{chave}}` contra o `data` da activity
 *    (`JSONLayoutParser+Helpers.swift:118`), então o dado viaja UMA vez.
 */
const COVER_DATA_KEY = "focusCover";

function coverElement(
  temCapa: boolean,
  width: number,
  height: number,
  radius: number,
  running: boolean,
): Record<string, unknown> {
  if (!temCapa) return glyphElement(running, Math.round(Math.min(width, height) * 0.86));
  return {
    type: "container",
    properties: [{ direction: "stack" }, { cornerRadius: radius }],
    children: [
      {
        type: "image",
        properties: [
          { base64: `{{${COVER_DATA_KEY}}}` },
          { resizable: true },
          { contentMode: "fit" },
          { width },
          { height },
        ],
      },
    ],
  };
}

/**
 * Barra da meta — SÓ no modo páginas, e isso não é preferência: é o que a
 * mantém honesta.
 *
 * O `progress` do Kit é `ProgressView(value:total:)` ESTÁTICO, e o §4.6 do
 * blueprint bloqueou a barra por isso — mas o que ele mediu foi uma barra de
 * TEMPO, que precisaria de update periódico e congelaria no instante em que o
 * app sai da frente (a WebView é suspensa). A barra de PÁGINAS não tem esse
 * problema: ela só muda quando a pessoa declara "+1", e cada declaração JÁ
 * reabre a activity (entra na `contentKey`). Zero update, zero mentira.
 */
function progressElement(
  snapshot: FocusLiveActivitySnapshot,
  height: number,
): Record<string, unknown> {
  return {
    type: "progress",
    properties: [
      { value: Math.min(snapshot.declaredCount, snapshot.goalTarget) },
      { total: snapshot.goalTarget },
      { color: ACCENT },
      { height },
    ],
  };
}

/**
 * Controles do card — a fileira de ações que a Ilha expandida e a tela de
 * bloqueio desenham (nível 3, opção (c) da decisão de 23/08).
 *
 * ⚠️ Rótulo e destino saem DAQUI, não do Swift, e isso é deliberado: o widget
 * recebe strings prontas e só as desenha. Se a cópia morasse no SwiftUI, mudar
 * "Pausar" para "Pausar sessão" passaria a exigir build novo das lojas — o
 * oposto do modelo que este arquivo mantém, em que 100% do que se lê no card é
 * decidido em TypeScript.
 *
 * A ação primária TROCA com o estado (pausar ↔ retomar) porque o card é a
 * única superfície onde a pessoa não vê o botão da tela para saber em que
 * estado está.
 */
export function focusActionsOf(snapshot: FocusLiveActivitySnapshot): Record<string, string> {
  const primaria: FocusDeepLinkAction = snapshot.paused ? "resume" : "pause";
  const stale = resolveStaleState(snapshot);
  return {
    actionPrimaryLabel: snapshot.paused ? "Retomar" : "Pausar",
    actionPrimaryUrl: buildFocusActionUrl(primaria, snapshot.sessionId),
    actionSecondaryLabel: "Encerrar",
    actionSecondaryUrl: buildFocusActionUrl("end", snapshot.sessionId),
    // Uma chave só para a cor: o widget não pode ter cópia da paleta (seria a
    // segunda fonte de verdade da marca dentro do card).
    actionTint: ACCENT,
    // ⚠️ O estado OBSOLETO viaja junto desde o start, e não no vencimento
    // (F7): quando o `staleDate` chega, a WebView está suspensa e não há
    // ninguém para mandar nada. Sem isto o card fica prometendo "Pausar" numa
    // sessão que já acabou.
    staleTitle: stale.title,
    ...(stale.actionLabel && stale.actionUrl
      ? { staleActionLabel: stale.actionLabel, staleActionUrl: stale.actionUrl }
      : {}),
  };
}

/**
 * Teto do data URL da capa.
 *
 * ⚠️ O guard mora AQUI, e não no payload inteiro, porque **medir o JSON cru
 * mede a coisa errada** — foi o que a primeira versão desta leva fazia, e a
 * medição a corrigiu. O plugin comprime `layout`/`dynamicIslandLayout`/
 * `behavior` (zlib + base64) assim que o total passa de 3 KB, e só eles: o
 * `data` viaja cru. Medido, com uma capa de 1.580 chars:
 *
 *   | caso (capa de 1.580 chars)     | comprime | attributes | state | total iOS |
 *   |--------------------------------|----------|------------|-------|-----------|
 *   | pomodoro, título curto, s/ capa | não     |      2.023 |   246 |     2.305 |
 *   | pomodoro, título curto          | sim     |      1.193 | 1.865 |     3.094 |
 *   | páginas, título longo, s/ capa  | não     |      2.571 |   246 |     2.853 |
 *   | páginas, título longo           | sim     |      1.473 | 1.865 |     3.374 |
 *
 * Ou seja: o pior caso com capa chega a 3.374 de ~4.096 — 18% de folga —
 * enquanto o JSON cru dele (4.7 KB) faria um guard ingênuo rejeitar a capa
 * **sempre**. É a compressão que paga a conta, e ela some justamente quando o
 * payload é pequeno o bastante para não precisar dela.
 *
 * O número: 4096 (teto) − 410 (10% de margem) − 1.560 (attributes comprimidos
 * no pior caso, medidos em 1.552 em 06/09 e arredondados para cima) − 36 (ids)
 * − 348 (sessionId + controles + o estado obsoleto, todos crus no state) =
 * 1.742 → **1700**. Uma capa real a 60×90 q0,5 mede ~1.500 chars, então o teto
 * aceita variação de arte densa e recusa o que comeria a margem — e desde
 * 05/09 a capa que passa do teto não é descartada: `focusCover.ts` desce uma
 * escada de qualidade/tamanho até caber (BUG-215).
 *
 * ⚠️ Este número depende do tamanho do `data` E dos attributes, e já caiu TRÊS
 * vezes por isso: 2200 → 1900 quando os controles nasceram (+246 B), 1900 →
 * 1750 quando a F7 acrescentou o estado obsoleto (+135 B no pior ramo, o
 * `done`), e 1750 → **1700** quando o layout de 05/09 ganhou a legenda da
 * contagem e a linha de contexto (attributes comprimidos 1.473 → 1.540). Campo
 * novo no ContentState ou texto novo no layout exige remedir — o guard de
 * orçamento `focusLiveActivityBudget.test.ts` REPRODUZ a compressão do plugin
 * sobre o payload real e cobra que a conta feche, e `focusStaleWidget.test.ts`
 * guarda a conta registrada.
 *
 * ⚠️ O teto de 4 KB é documentação da Apple, não medição desta sessão — é por
 * isso que o guard existe. Estourar não degrada: `Activity.request` recusa e
 * **não sai Ilha nenhuma**. Passou do teto, a capa cai e o layout usa o glifo.
 */
export const FOCUS_COVER_MAX_BASE64_CHARS = 1700;

/**
 * Geometria da Ilha (pt), travada por teste porque foi MEDIDA em aparelho
 * (06/09), não escolhida:
 *
 *  · **expandida** — teto de 160pt de altura (Apple). A linha de cima é
 *    `max(capa, coluna da contagem)` ao lado da câmera, e abaixo dela vêm o
 *    título, o contexto e os chips. Com a capa a 60pt o conjunto passava do
 *    teto e os chips saíam CORTADOS (captura do fundador); a 48pt sobra ~10pt.
 *    `focusLiveActivity.test.ts` soma a altura a partir do payload e do Swift.
 *  · **compacta** — a região tem ~37pt de altura; a capa vai a 27 para não ser
 *    um selo de 24 num espaço de 37. O timer ganha TETO de largura: 6 dígitos +
 *    dois-pontos a 14pt semibold monoespaçado medem ~44pt ("240:00", o maior
 *    que um pomodoro de 4h mostra com `showsHours: false`), e o `maxWidth`
 *    corta a reserva que o SwiftUI faz além disso — era ela que esticava a
 *    Ilha com um vazio à direita do número.
 */
export const FOCUS_ISLAND_COVER_PT = { width: 32, height: 48 } as const;
export const FOCUS_COMPACT_COVER_PT = { width: 18, height: 27 } as const;
export const FOCUS_COMPACT_TIMER_MAX_WIDTH_PT = 52;

/** Monta o payload com (ou sem) capa. O guard de orçamento é o chamador. */
function composeFocusActivityPayload(
  snapshot: FocusLiveActivitySnapshot,
  nowMs: number,
  coverDataUrl: string | null,
): Record<string, unknown> {
  const running = !snapshot.paused;
  const goalLine = goalLineOf(snapshot);
  const sessionLine = sessionLineOf(snapshot);
  const temCapa = coverDataUrl !== null;
  const pausedMin = pausedMinutesOf(snapshot, nowMs);

  /** Texto secundário — a mesma tinta e o mesmo peso em toda legenda do card. */
  const caption = (content: string, fontSize: number): Record<string, unknown> =>
    text(content, [{ fontSize }, { fontWeight: "semibold" }, { color: SOFT }]);

  const titleElement = (fontSize: number): Record<string, unknown> =>
    text(bookLine(snapshot), [{ fontSize }, { fontWeight: "bold" }, { color: INK }, { lineLimit: 1 }]);

  const statusElement = running
    ? timerElement(snapshot, [
        { fontSize: 26 },
        { fontWeight: "semibold" },
        { color: ACCENT },
        // Sem isto o número DANÇA a cada segundo: a fonte do sistema é
        // proporcional, e "11" é mais estreito que "00".
        { monospacedDigit: true },
      ])
    : text(`Pausado · ${pausedMin} min de foco`, [
        { fontSize: 16 },
        { fontWeight: "semibold" },
        { color: SOFT },
      ]);

  /**
   * A linha sob a contagem da tela de bloqueio: o que o número é ("restante")
   * mais o que a sessão é ("Pomodoro de 25 min"). No modo páginas com meta a
   * linha de meta + barra assumem esse papel — repetir seria ruído.
   */
  const lockContextLine = running
    ? [caption(sessionLine ? `${timerCaptionOf(snapshot)} · ${sessionLine}` : timerCaptionOf(snapshot), 12)]
    : sessionLine
      ? [caption(sessionLine, 12)]
      : [];

  /**
   * Tela de bloqueio: capa à esquerda, coluna de texto à direita.
   *
   * ⚠️ `insideAlignment: "leading"` é obrigatório na coluna — o default do Kit
   * é `.center` (`JSONLayoutParser+Container.swift`, ramo `default`), e era ele
   * que deixava marca, título e contagem CENTRALIZADOS num card que é lido da
   * esquerda para a direita.
   */
  const layout = {
    type: "container",
    properties: [
      { direction: "horizontal" },
      { spacing: 12 },
      { insideAlignment: "center" },
      { padding: 14 },
    ],
    children: [
      coverElement(temCapa, 44, 66, 5, running),
      {
        type: "container",
        properties: [
          { direction: "vertical" },
          { spacing: 3 },
          { insideAlignment: "leading" },
          // Empurra a coluna a ocupar a largura restante; sem isto o HStack
          // encolhe ao conteúdo e o conjunto flutua no meio do card. ⚠️ O
          // `alignment` é OBRIGATÓRIO junto: o `FrameModifier` do Kit alinha
          // `.center` por padrão (medido em `JSONLayoutParser+BuildView.swift`,
          // ramo `default`), então o frame largo centralizava a coluna — sem
          // sintoma enquanto o timer a esticava, visível na pausa (06/09).
          { maxWidth: 10000 },
          { alignment: "leading" },
        ],
        children: [
          text("FOCO · LIVRA", [{ fontSize: 10 }, { fontWeight: "semibold" }, { color: SOFT }]),
          titleElement(15),
          statusElement,
          ...lockContextLine,
          ...(goalLine ? [progressElement(snapshot, 5), caption(goalLine, 12)] : []),
        ],
      },
    ],
  };

  /**
   * Ilha COMPACTA (06/09, relato: *"a capa na full esquerda e o tempo colado
   * na esquerda da aba da direita"*). Capa à esquerda da câmera e contagem à
   * direita é a anatomia do iOS (é o desenho do Timer e do Music). O defeito é
   * MEDIDO no SwiftUI: `Text(timerInterval:)` reserva a largura do texto mais
   * largo que pode vir a mostrar e alinha os dígitos à ESQUERDA desse frame —
   * daí o número encostado na câmera com um vazio à direita. Duas propriedades
   * corrigem: `alignment: "trailing"` (vira `multilineTextAlignment` no
   * `TextModifier` E o `alignment` do frame) e `maxWidth`, que impede a reserva
   * de esticar a Ilha. A contagem passa à tinta da marca, como na expandida.
   */
  const compactTrailing = running
    ? timerElement(snapshot, [
        { fontSize: 14 },
        { fontWeight: "semibold" },
        { color: ACCENT },
        { monospacedDigit: true },
        { alignment: "trailing" },
        { maxWidth: FOCUS_COMPACT_TIMER_MAX_WIDTH_PT },
      ])
    : text("Pausado", [{ fontSize: 12 }, { fontWeight: "semibold" }, { color: SOFT }]);

  /**
   * Ilha expandida (05/09, relato do fundador: *"capa jogada à esquerda e o
   * tempo correndo na direita, muito afastadas, sem infos o suficiente"*).
   *
   * `.leading` e `.trailing` flanqueiam a CÂMERA — a distância entre eles é
   * anatomia do iOS, não escolha. O que dá para fazer é cada lado dizer mais:
   *
   *  · `.leading`  — a capa (32×48 desde 06/09; 40×60 em 05/09), da altura da
   *    coluna que a contagem ocupa do outro lado, para os dois lados pesarem
   *    igual. ⚠️ A altura é ORÇAMENTO, não gosto — ver `FOCUS_ISLAND_COVER_PT`;
   *  · `.trailing` — a contagem com LEGENDA embaixo ("restante" / "de leitura"),
   *    alinhada à direita: um número sozinho não diz se conta para cima ou para
   *    baixo. Pausado vira "Pausado" + "N min de foco";
   *  · `.bottom`   — título + a linha de contexto (alvo do pomodoro, sessão
   *    livre, ou meta + barra no modo páginas). É a faixa LARGA; o título já
   *    tinha saído do `.center` estreito em 23/08.
   *
   * `insideAlignment: "trailing"` em container vertical é valor MEDIDO no Kit
   * (`JSONLayoutParser+Container.swift`, ramo `vertical`: "trailing" | "right").
   */
  const expandedTrailing: Record<string, unknown> = {
    type: "container",
    properties: [{ direction: "vertical" }, { spacing: 1 }, { insideAlignment: "trailing" }],
    children: running
      ? [
          timerElement(snapshot, [
            { fontSize: 22 },
            { fontWeight: "semibold" },
            { color: ACCENT },
            { monospacedDigit: true },
            // Sem isto o número e a legenda DESALINHAM (captura de 06/09): o
            // timer expande à largura da região com os dígitos à esquerda,
            // enquanto a legenda, do tamanho do texto, encosta à direita.
            { alignment: "trailing" },
          ]),
          caption(timerCaptionOf(snapshot), 10),
        ]
      : [
          text("Pausado", [{ fontSize: 14 }, { fontWeight: "semibold" }, { color: SOFT }]),
          caption(`${pausedMin} min de foco`, 10),
        ],
  };

  const expanded: Record<string, unknown> = {
    leading: coverElement(
      temCapa,
      FOCUS_ISLAND_COVER_PT.width,
      FOCUS_ISLAND_COVER_PT.height,
      4,
      running,
    ),
    trailing: expandedTrailing,
    // Região `bottom` MEDIDA no Kit (JSONLayoutParser+Models.swift:40 e
    // DynamicActivityWidget.swift:71 — `DynamicIslandExpandedRegion(.bottom)`).
    bottom: {
      type: "container",
      properties: [
        { direction: "vertical" },
        { spacing: 2 },
        { insideAlignment: "leading" },
        // Medido no aparelho (05/09): sem largura, a coluna encolhe ao conteúdo
        // e o iOS a CENTRALIZA na faixa. ⚠️ E o `maxWidth` SOZINHO não cura —
        // foi a leitura errada de 05/09 (o registro dizia que o `FrameModifier`
        // alinha `.topLeading` por padrão; medido em
        // `JSONLayoutParser+BuildView.swift`, o `default` é `.center`, e a
        // captura de 06/09 mostrou título e contexto AINDA centralizados, com
        // os chips à esquerda). O `alignment` é a metade que faltava.
        { maxWidth: 10000 },
        { alignment: "leading" },
      ],
      children: [
        titleElement(15),
        ...(goalLine
          ? [progressElement(snapshot, 4), caption(goalLine, 11)]
          : sessionLine
            ? [caption(sessionLine, 11)]
            : []),
      ],
    },
  };

  return {
    layout,
    dynamicIslandLayout: {
      expanded,
      compactLeading: coverElement(
        temCapa,
        FOCUS_COMPACT_COVER_PT.width,
        FOCUS_COMPACT_COVER_PT.height,
        3,
        running,
      ),
      compactTrailing,
      // `minimal` é um círculo de ~22pt — capa ali sairia recortada no meio.
      minimal: glyphElement(running, 16),
    },
    behavior: {
      // String vazia = URL nula no Swift = toque abre o app no comportamento
      // padrão. Esquema custom aqui quebraria o toque (o app não o reivindica).
      widgetUrl: "",
      backgroundTint: PAPER,
      systemActionForegroundColor: INK,
    },
    /**
     * Opção de TOPO do startActivity, em ms de JS — medido no plugin
     * (`LiveActivitiesPlugin.swift:203-204`: `call.getDouble("staleDate")` →
     * `Date(timeIntervalSince1970: $0 / 1000)`). O `relevanceScore` do mesmo
     * caminho fica de fora DE PROPÓSITO: ele ordena activities concorrentes do
     * mesmo app, e este app tem no máximo UMA (a varredura do BUG-163 garante).
     */
    staleDate: staleDateMsOf(snapshot),
    data: {
      // A ADOÇÃO pós-relaunch reconhece de quem é a activity por aqui.
      sessionId: snapshot.sessionId,
      // E é aqui que a capa viaja — UMA vez, referenciada por `{{focusCover}}`
      // nas três superfícies. Embutida no layout ela apareceria triplicada e
      // estourava o orçamento sozinha (medido).
      ...(temCapa ? { [COVER_DATA_KEY]: coverDataUrl } : {}),
      // Os controles: rótulo e destino decididos aqui, desenhados no Swift.
      ...focusActionsOf(snapshot),
    },
  };
}

/**
 * Payload completo do `startActivity`. Puro e exportado — é o que os testes
 * travam (chaves do DSL medidas no parser Swift: `endTime` em ms, `style`,
 * `systemName`, propriedades como array de objetos de chave única).
 *
 * A capa acima do teto é descartada aqui, e não no chamador: estourar o limite
 * do ActivityKit não degrada — derruba a Ilha inteira —, e deixar essa decisão
 * para quem chama é dar a N call sites a chance de esquecê-la.
 */
export function buildFocusActivityPayload(
  snapshot: FocusLiveActivitySnapshot,
  nowMs: number,
): Record<string, unknown> {
  const capa =
    snapshot.coverDataUrl && snapshot.coverDataUrl.length <= FOCUS_COVER_MAX_BASE64_CHARS
      ? snapshot.coverDataUrl
      : null;
  return composeFocusActivityPayload(snapshot, nowMs, capa);
}

/* ── Memória do reconciliador ───────────────────────────────────────────── */

interface ActivityMemory {
  activityId: string;
  sessionId: string;
  paused: boolean;
  /** Assinatura do conteúdo que exige reabrir (alvo estendido muda o fim). */
  contentKey: string;
}

let memory: ActivityMemory | null = null;
let bootstrapped = false;
/**
 * Primeira convergência do processo ainda não varreu o que sobrou do processo
 * anterior. Existe separado de `bootstrapped` porque a adoção (§3.5 do
 * blueprint) sai ANTES de qualquer chamada ao plugin: sem este sinalizador, o
 * relaunch que adota a activity certa nunca olharia se havia outras junto.
 */
let needsOrphanReap = false;
/**
 * Uma convergência ficou por fazer porque o app estava no FUNDO (ou o start
 * foi recusado). A volta ao primeiro plano a dispara de novo — regra 5.
 */
let retryWhenVisible = false;
let visibilityHandler: (() => void) | null = null;

/* ── Fila de convergência (BUG-163) ────────────────────────────────────────
 *
 * O chamador é um efeito que dispara com `void`, e `useFocusTimer` grava a
 * sessão DUAS vezes em cada transição (otimista + confirmação do servidor).
 * Sem fila, as duas convergências se cruzavam na janela em que `memory` está
 * `null` — entre encerrar e persistir a nova id — e cada uma iniciava a sua
 * activity; a última a persistir apagava a id da outra, que ficava viva e
 * inalcançável. Era isso que empilhava cards na tela de bloqueio.
 *
 * `pending` guarda só a foto MAIS RECENTE: o par otimista→servidor vira uma
 * convergência, não duas, e a Ilha para de piscar a cada transição.
 */
let pending: FocusLiveActivitySnapshot | null | undefined;
let chain: Promise<void> = Promise.resolve();

/**
 * Assinatura do conteúdo que exige reabrir a activity (o layout mora nos
 * attributes, imutáveis). `declaredCount` entra desde 21/08: cada "+1" do modo
 * páginas reabre com o contador novo — é gesto, não update periódico, e chega
 * com o app na frente, onde a Ilha nem renderiza. ⚠️ Mudar o FORMATO desta
 * chave invalida a adoção pós-update UMA vez (a stored key antiga não casa e a
 * activity reabre) — inofensivo, mas é bom saber por que a Ilha piscou.
 */
function contentKeyOf(
  snapshot: FocusLiveActivitySnapshot,
  coverPresent: boolean = snapshot.coverDataUrl != null,
): string {
  return [
    snapshot.paused ? "p" : "r",
    snapshot.targetDurationSec,
    snapshot.pausedTotalSec,
    snapshot.goalUnit,
    snapshot.goalTarget,
    snapshot.declaredCount,
    // PRESENÇA da capa, não o conteúdo dela: a capa é do livro da sessão e não
    // muda no meio: o que muda é ela CHEGAR (a conversão do canvas assenta
    // depois do início). Guardar o data URL inteiro aqui jogaria ~1,5 KB no
    // localStorage a cada convergência para responder à mesma pergunta.
    coverPresent ? "capa" : "-",
    // O TÍTULO (BUG-215): o layout mora nos attributes, então mudar o nome do
    // livro exige reabrir. Sem ele aqui, a recuperação que resolve o exemplar
    // DEPOIS da primeira convergência ("Sessão de leitura" → "Duna") deixava
    // a Ilha com o nome antigo até a sessão acabar. Vai por último: pode
    // conter ":" e a chave é só comparada, nunca partida.
    snapshot.bookTitle?.trim() || "",
  ].join(":");
}

function readStored(): ActivityMemory | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ActivityMemory>;
    if (typeof parsed.activityId !== "string" || typeof parsed.sessionId !== "string") return null;
    return {
      activityId: parsed.activityId,
      sessionId: parsed.sessionId,
      paused: parsed.paused === true,
      contentKey: typeof parsed.contentKey === "string" ? parsed.contentKey : "",
    };
  } catch {
    return null;
  }
}

function persist(next: ActivityMemory | null): void {
  memory = next;
  try {
    if (next) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage indisponível — a memória de módulo segura a sessão corrente */
  }
}

function isSupportedHere(): boolean {
  return FOCUS_LIVE_ACTIVITY_ENABLED && nativePlatform() === "ios";
}

/**
 * Primeira chamada do processo: herda (ou limpa) o que um processo anterior
 * deixou vivo. O plugin re-registra as activities existentes no load, então
 * `endActivity` com o id guardado funciona depois do relaunch.
 */
function bootstrap(): void {
  if (bootstrapped) return;
  bootstrapped = true;
  needsOrphanReap = true;
  const stored = readStored();
  if (stored) memory = stored;
  installPushTokenListener();
  installVisibilityListener();
}

/**
 * `hidden` é o único sinal que o WKWebView dá de "o app saiu da frente" sem
 * arrastar `@capacitor/app` para dentro do módulo do payload. Ausente (SSR,
 * runner sem DOM) conta como visível — a regra 5 é otimização de quem tem
 * aparelho, não gate de quem não tem.
 */
function isDocumentHidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

function installVisibilityListener(): void {
  if (visibilityHandler || typeof document === "undefined") return;
  visibilityHandler = () => {
    if (document.visibilityState !== "visible" || !retryWhenVisible || !lastConverged) return;
    retryWhenVisible = false;
    /*
     * 🔴 Foto que JÁ VENCEU não reabre (BUG-232). A volta ao primeiro plano
     * depois do vencimento é exatamente quando o aviso trouxe a pessoa de
     * volta: reabrir uma activity "rodando" em 0:00 gerava token novo →
     * registro com `dueMs` no passado (dentro da folga) → task para JÁ → um
     * SEGUNDO alerta com som, antes de o `completeFocusSession` (5-6 s)
     * gravar `settling`. O timer já está indo ao wrap-up pelo mesmo
     * `visibilitychange`, e o `converge(null)` dele varre o que houver.
     * `resolveStaleState` cobre os três vencimentos: alvo, pausa expirada e
     * teto de plausibilidade.
     */
    if (resolveStaleState(lastConverged).atMs <= Date.now()) return;
    void syncFocusLiveActivity(lastConverged);
  };
  document.addEventListener("visibilitychange", visibilityHandler);
}

/**
 * Já convergido? Igualdade de chave — E a regra 5(c): foto SEM capa sobre uma
 * activity COM capa da mesma foto não reabre. A capa só "chega" (a conversão
 * assenta depois do início); uma foto sem capa depois de uma com é a
 * conversão desistindo ou o prazo do modal esgotando, e trocar a Ilha certa
 * por uma pior para dizer a mesma coisa é churn, não convergência.
 */
function alreadyConverged(stored: string, snapshot: FocusLiveActivitySnapshot, key: string): boolean {
  if (stored === key) return true;
  return snapshot.coverDataUrl == null && stored === contentKeyOf(snapshot, true);
}

/* ── Push de conclusão (blueprint focus-live-activity-push.md) ───────────── */

/**
 * O que o módulo precisa de fora para registrar o token no servidor. Vem por
 * INJEÇÃO (o `FocusSessionProvider` instala), e não por import: este módulo é
 * importado pelo modal e pelos testes sem Firebase no grafo, e um `import()`
 * dinâmico aqui já pendurou no WKWebView uma vez (regra 3 acima).
 */
export interface FocusLiveActivityPushBridge {
  register(input: {
    sessionId: string;
    activityId: string;
    liveActivityToken: string;
    fcmToken: string | null;
    dueMs: number | null;
    data: Record<string, string | number | boolean>;
  }): Promise<unknown>;
  /** Registration token do FCM do aparelho — `null` quando o aparelho não dá. */
  getFcmToken(): Promise<string | null>;
}

let pushBridge: FocusLiveActivityPushBridge | null = null;
/** A última foto que CONVERGIU — é dela que saem `dueMs` e `data` do registro. */
let lastConverged: FocusLiveActivitySnapshot | null = null;
/** `${activityId}:${token}:${dueMs}` do último registro — o mesmo token duas vezes não custa callable. */
let lastRegistrationKey: string | null = null;
let pushTokenListenerInstalled = false;

export function installFocusLiveActivityPushBridge(bridge: FocusLiveActivityPushBridge | null): void {
  pushBridge = bridge;
}

/**
 * O vencimento que a ILHA conta — o mesmo `computeDueMs` do countdown, sobre a
 * foto convergida. Pausada e sem alvo cronometrado não vencem → `null`, e o
 * servidor só guarda o token.
 */
export function pushDueMsOf(snapshot: FocusLiveActivitySnapshot): number | null {
  if (snapshot.paused || snapshot.targetDurationSec <= 0) return null;
  return computeDueMs(
    {startedAtMs: snapshot.startedAtMs, pausedAtMs: null, pausedTotalSec: snapshot.pausedTotalSec},
    snapshot.targetDurationSec,
  );
}

/**
 * Token de push chegou para uma activity. Só a activity que a MEMÓRIA reconhece
 * interessa: token de órfã, ou de uma activity que a convergência seguinte já
 * substituiu, não vira registro. Pausa/retomada/+5 min reabrem a activity, então
 * o vencimento novo chega aqui sozinho, com o token novo — não há "re-registro"
 * a fazer por evento de sessão.
 */
async function onPushToken(activityId: string, token: string): Promise<void> {
  if (!pushBridge || !memory || memory.activityId !== activityId || !lastConverged) return;
  const snapshot = lastConverged;
  const dueMs = pushDueMsOf(snapshot);
  const key = `${activityId}:${token}:${dueMs ?? "-"}`;
  if (key === lastRegistrationKey) return;
  lastRegistrationKey = key;
  try {
    const payload = buildFocusActivityPayload(snapshot, Date.now()) as {
      data: Record<string, string | number | boolean>;
    };
    const fcmToken = await pushBridge.getFcmToken().catch(() => null);
    await pushBridge.register({
      sessionId: snapshot.sessionId,
      activityId,
      liveActivityToken: token,
      fcmToken,
      dueMs,
      data: payload.data,
    });
  } catch (err) {
    // Regra 1: mostrador nunca alcança a sessão. Sem registro, o card fica como
    // hoje — em 0:00 até abrir o app — e a notificação local (F5) segue avisando.
    console.warn("[Lura] Live Activity: registro do push de conclusão falhou (inofensivo).", err);
  }
}

function installPushTokenListener(): void {
  if (pushTokenListenerInstalled) return;
  pushTokenListenerInstalled = true;
  try {
    const plugin = LiveActivities as unknown as {
      addListener?: (
        event: string,
        handler: (data: {activityId?: unknown; token?: unknown}) => void,
      ) => unknown;
    };
    if (typeof plugin.addListener !== "function") return;
    // `Promise.resolve(...).catch`: o `try` só pega throw SÍNCRONO, e o plugin
    // devolve uma promise — rejeitada, ela vazava como unhandled rejection
    // (BUG-234). Regra 1: nada da Ilha alcança a sessão.
    Promise.resolve(
      plugin.addListener("pushTokenUpdate", (data) => {
      if (typeof data?.activityId !== "string" || typeof data?.token !== "string") return;
      void onPushToken(data.activityId, data.token);
      }),
    ).catch((err) => {
      console.warn("[Lura] Live Activity: listener de token rejeitou (inofensivo).", err);
    });
  } catch (err) {
    console.warn("[Lura] Live Activity: listener de token indisponível (inofensivo).", err);
  }
}

/**
 * Encerra as activities do app, opcionalmente preservando UMA.
 *
 * Varre `getAllActivities` em vez de encerrar só a id lembrada, e a diferença é
 * o BUG-163 inteiro: uma vez que uma activity vaza (processo morto entre o
 * start e o persist, `endActivity` que falhou, corrida antes da fila existir),
 * o app deixava de conhecer a id dela e NADA mais a alcançava — nem encerrar a
 * sessão. É esta varredura que limpa o que já está preso nos aparelhos.
 *
 * `keepId` serve ao reap de bootstrap: preserva a activity adotada e mata só as
 * extras, para o relaunch não reabrir a Ilha à toa.
 */
async function endActivities(
  keepId: string | null,
  alsoEnd: readonly string[] = [],
): Promise<void> {
  const targets = new Set<string>();
  const remembered = memory?.activityId ?? null;
  if (remembered && remembered !== keepId) targets.add(remembered);
  // A activity que a reabertura acabou de SUBSTITUIR: a memória já aponta para
  // a nova, então ela precisa vir por nome — sem isto, plugin sem
  // `getAllActivities` deixaria a antiga viva (a Ilha empilhando de novo).
  for (const id of alsoEnd) if (id !== keepId) targets.add(id);

  try {
    for (const id of await liveActivitiesFacade.list()) {
      if (id !== keepId) targets.add(id);
    }
  } catch (err) {
    // Plugin antigo (sem o método) ou ponte muda: degrada para "encerra só a
    // lembrada", que é o comportamento anterior a este fix.
    console.warn("[Lura] Live Activity: getAllActivities indisponível.", err);
  }

  if (!keepId) persist(null);

  for (const id of targets) {
    try {
      await liveActivitiesFacade.end(id);
    } catch (err) {
      console.warn("[Lura] Live Activity: endActivity falhou (inofensivo).", err);
    }
  }
}

/**
 * Faz a Live Activity convergir para a foto recebida.
 *
 *  - `null` = não há sessão viva para mostrar (wrap-up, cancelada, ghost) →
 *    encerra o que houver.
 *  - Foto de sessão → garante activity correspondente (adota a do relaunch
 *    quando a sessão é a mesma e o estado bate; reabre quando pausa/alvo
 *    mudaram; inicia quando não há nenhuma).
 *
 * Fora do iOS nativo (ou com o interruptor desligado) é no-op puro.
 *
 * ⚠️ Não chame esta função direto — ela pressupõe que ninguém mais está
 * convergindo. Quem serializa é `syncFocusLiveActivity`.
 */
async function converge(snapshot: FocusLiveActivitySnapshot | null): Promise<void> {
  try {
    bootstrap();

    if (!snapshot) {
      needsOrphanReap = false;
      lastConverged = null;
      retryWhenVisible = false;
      // Encerrar funciona também no fundo (só o `request` exige primeiro plano).
      await endActivities(null);
      return;
    }

    // A foto que vale para o registro do push é a que CONVERGIU — inclusive na
    // adoção pós-relaunch, em que o token pode chegar de novo (rotação).
    lastConverged = snapshot;
    const key = contentKeyOf(snapshot);
    if (memory && memory.sessionId === snapshot.sessionId && alreadyConverged(memory.contentKey, snapshot, key)) {
      // Já convergido — inclui a adoção pós-relaunch. Aqui NÃO se reabre nada
      // (reabrir seria churn visível na Ilha a cada volta do app), mas é a
      // única passagem em que dá para varrer o que o processo anterior deixou:
      // sem isso, quem relança com órfãs presas as mantém até a sessão acabar.
      if (needsOrphanReap) {
        needsOrphanReap = false;
        await endActivities(memory.activityId);
      }
      return;
    }

    // Regra 5(a): no fundo o `Activity.request` é recusado, e o `catch` antigo
    // apagava a memória — a Ilha da mesma sessão (se havia) virava órfã e a
    // nova não nascia. Guarda a foto; a volta ao primeiro plano converge.
    if (isDocumentHidden()) {
      retryWhenVisible = true;
      return;
    }

    needsOrphanReap = false;
    const previous = memory?.activityId ?? null;
    let activityId: string;
    try {
      // Regra 5(b): abre ANTES de varrer. A Ilha aparece uma ida à ponte mais
      // cedo, e se o start falhar a anterior segue viva.
      activityId = await liveActivitiesFacade.start(buildFocusActivityPayload(snapshot, Date.now()));
    } catch (err) {
      // Regra 1: a Ilha nunca derruba a sessão (iOS < 16.2, Live Activities
      // desligadas em Ajustes, plugin ausente, ou o app que acabou de sair da
      // frente). A memória de uma activity viva da MESMA sessão fica — ela é
      // melhor que nada; a de outra sessão é órfã e vai.
      console.warn("[Lura] Live Activity: start recusado — tenta de novo no primeiro plano.", err);
      retryWhenVisible = true;
      if (memory && memory.sessionId !== snapshot.sessionId) await endActivities(null);
      return;
    }
    persist({ activityId, sessionId: snapshot.sessionId, paused: snapshot.paused, contentKey: key });
    retryWhenVisible = false;
    await endActivities(activityId, previous ? [previous] : []);
  } catch (err) {
    console.warn("[Lura] Live Activity indisponível nesta sessão.", err);
  }
}

/**
 * Porta única do reconciliador — serializa e COALESCE.
 *
 * Serializa porque duas convergências ao mesmo tempo cruzam a janela em que a
 * memória está vazia e cada uma abre a sua activity (BUG-163). Coalesce porque
 * cada transição de sessão chega em par (otimista + servidor): guardando só a
 * foto mais recente, o par vira uma convergência e a Ilha não pisca.
 *
 * A promise devolvida cobre a convergência que ESTA chamada pediu — é o que
 * deixa o teste `await`-ar sem conhecer a fila.
 */
export function syncFocusLiveActivity(
  snapshot: FocusLiveActivitySnapshot | null,
): Promise<void> {
  if (!isSupportedHere()) return Promise.resolve();
  pending = snapshot;
  /**
   * O `.catch` não é decoração: a cadeia é o ÚNICO caminho de convergência, e
   * uma rejeição que sobrevivesse aqui a envenenaria para sempre — todo
   * `.then` seguinte pularia o passo e devolveria promise rejeitada, com o
   * `void` do chamador virando unhandled rejection. `converge` já engole tudo,
   * então isto é o cinto que mantém a propriedade verdadeira se alguém mexer
   * lá dentro.
   */
  chain = chain.then(drainOnce).catch((err) => {
    console.warn("[Lura] Live Activity: convergência falhou (inofensivo).", err);
  });
  return chain;
}

/**
 * Um passo da fila. Se uma chamada posterior já consumiu a foto (coalescing),
 * este passo é no-op — foi ela que aplicou o estado mais novo.
 */
async function drainOnce(): Promise<void> {
  if (pending === undefined) return;
  const next = pending;
  pending = undefined;
  await converge(next);
}

/** Só para os testes — zera a memória do módulo. */
export function __resetFocusLiveActivityForTests(): void {
  memory = null;
  bootstrapped = false;
  needsOrphanReap = false;
  pending = undefined;
  chain = Promise.resolve();
  lastConverged = null;
  lastRegistrationKey = null;
  pushTokenListenerInstalled = false;
  pushBridge = null;
  retryWhenVisible = false;
  if (visibilityHandler && typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", visibilityHandler);
  }
  visibilityHandler = null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* idem */
  }
}
